import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, of } from 'rxjs';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroArrowUturnLeft, heroCreditCard } from '@ng-icons/heroicons/outline';
import {
  ToastrService,
  UiAttainmentBar,
  UiBadge,
  UiButton,
  UiConfirmDialogService,
  UiEmptyState,
  UiHint,
  UiPanelCard,
  UiSectionHeader,
  UiSkeletonGrid,
} from '../../../shared/ui';
import type { BillingCapacityRow } from '../../../core/api/generated/models/BillingCapacityRow';
import type { BillingMeSubscriptionResponse } from '../../../core/api/generated/models/BillingMeSubscriptionResponse';
import type { BillingMePaymentMethodResponse } from '../../../core/api/generated/models/BillingMePaymentMethodResponse';
import { AuthStore } from '../../../core/auth/auth.store';
import { IfPermissionDirective } from '../../../core/auth/permission.directive';
import { BillingApiService } from '../../billing/billing-api.service';
import { InvoicesSectionComponent } from '../../billing/pages/invoices-section.component';
import { formatMicros } from '../../billing/pages/credit-view';

type Tone = 'success' | 'warning' | 'danger' | 'neutral' | 'info';

/** Acima disto, o recurso está "perto do limite" e vale sugerir upgrade. Mesmo limiar do teto de gasto. */
const CAPACITY_WARN_PCT = 80;

interface PlanView {
  name: string;
  statusLabel: string;
  statusTone: Tone;
  cycleLabel: string;
  renewalLabel: string | null;
  periodEndLabel: string | null;
  /** null quando o catálogo não tem preço flat pro plano (ex.: "sob consulta"). */
  priceLabel: string | null;
}

interface CapacityRowView {
  /** Chave estável do recurso (track) — o rótulo é só apresentação. */
  resource: string;
  label: string;
  usageLabel: string;
  realized: number;
  /** Meta da barra; 0 = sem limite (ilimitado) → não desenha barra. */
  target: number;
  unlimited: boolean;
  tone: Tone;
  statusLabel: string | null;
  /** Perto/no limite → oferece o CTA de upgrade contextual. */
  showUpgrade: boolean;
}

const STATUS_LABEL: Record<string, string> = {
  free: 'Gratuito',
  trial: 'Em teste',
  active: 'Ativa',
  past_due: 'Pagamento pendente',
  suspended: 'Suspensa',
  cancelled: 'Cancelada',
  expired: 'Teste expirado',
};

const STATUS_TONE: Record<string, Tone> = {
  free: 'neutral',
  trial: 'info',
  active: 'success',
  past_due: 'warning',
  suspended: 'danger',
  cancelled: 'neutral',
  expired: 'danger',
};

function formatBRL(amount: string, currency = 'BRL'): string {
  return Number(amount).toLocaleString('pt-BR', { style: 'currency', currency });
}

const CYCLE_LABEL: Record<string, string> = {
  monthly: 'Mensal',
  quarterly: 'Trimestral',
  semiannual: 'Semestral',
  annual: 'Anual',
};

function formatInt(value: number): string {
  return value.toLocaleString('pt-BR');
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

@Component({
  selector: 'app-subscription-settings-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIcon,
    RouterLink,
    IfPermissionDirective,
    InvoicesSectionComponent,
    UiAttainmentBar,
    UiBadge,
    UiButton,
    UiEmptyState,
    UiHint,
    UiPanelCard,
    UiSectionHeader,
    UiSkeletonGrid,
  ],
  providers: [provideIcons({ heroArrowUturnLeft, heroCreditCard })],
  templateUrl: './subscription-settings.page.html',
})
export class SubscriptionSettingsPage {
  private readonly api = inject(BillingApiService);
  private readonly auth = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toast = inject(ToastrService);
  private readonly confirmDialog = inject(UiConfirmDialogService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly actionPending = signal(false);
  private readonly subscription = signal<BillingMeSubscriptionResponse | null>(null);
  private readonly capacity = signal<BillingCapacityRow[]>([]);
  private readonly paymentMethod = signal<BillingMePaymentMethodResponse | null>(null);
  /** `me/subscription.plan` não traz preço — só o catálogo tem. Busca à parte pra achar o flat price. */
  private readonly planFlatPriceLabel = signal<string | null>(null);
  private readonly currency = signal<string>('BRL');

  readonly plan = computed<PlanView | null>(() => this.toPlanView());
  readonly canManage = computed<boolean>(() => this.auth.hasEveryPermission(['billing:subscription:manage']));

  /** Cancelamento agendado para o fim do ciclo (sem reembolso) — habilita "Reativar". */
  readonly cancelScheduled = computed<boolean>(() => this.subscription()?.subscription?.cancel_at_period_end === true);
  /** Downgrade agendado para o fim do ciclo — mostra aviso + permite cancelar a troca (reativa o plano atual). */
  readonly downgradeScheduled = computed<{ planName: string; atLabel: string | null } | null>(() => {
    const sub = this.subscription()?.subscription;
    if (!sub?.scheduled_plan_id || !sub.scheduled_plan_name) return null;
    return { planName: sub.scheduled_plan_name, atLabel: formatDate(sub.scheduled_at) };
  });
  /** Reativar só desfaz uma alteração AGENDADA (cancelamento ou downgrade) ainda não aplicada. */
  readonly canReactivate = computed<boolean>(() => this.cancelScheduled() || this.downgradeScheduled() !== null);
  /**
   * "Cancelar" só agenda pra fim do ciclo — e o sweep que aplica isso (`renewDuePeriods`) só
   * processa assinaturas `active` (trial cai nele mais tarde, ao converter). Em `suspended`/
   * `past_due` o agendamento nunca seria varrido/aplicado, então o botão nem aparece.
   */
  readonly canCancel = computed<boolean>(() => {
    const status = this.subscription()?.subscription?.status;
    return (status === 'active' || status === 'trial') && !this.cancelScheduled();
  });

  /** Plano no modo crédito incluso geral (credit_pool) → hero de crédito. */
  readonly creditMode = computed<boolean>(() => this.subscription()?.plan?.allowance_mode === 'credit_pool');
  /** "R$X" do crédito incluso do plano (0 = sem crédito). */
  readonly includedCreditLabel = computed<string>(() =>
    formatMicros(this.subscription()?.plan?.included_credit_micros ?? 0, this.currency()),
  );

  /** Limites do plano (usuários/agentes/canais): uso real vs limite, com CTA de upgrade ao encostar. */
  readonly capacityRows = computed<CapacityRowView[]>(() =>
    this.capacity().map((row) => {
      const unlimited = row.limit === null || row.limit === undefined;
      const limit = unlimited ? 0 : row.limit!;
      const pct = unlimited ? 0 : Math.round((row.current / Math.max(1, limit)) * 100);
      const at = !unlimited && row.current >= limit;
      const near = !unlimited && !at && pct >= CAPACITY_WARN_PCT;
      return {
        resource: row.resource,
        label: row.label,
        // Só os números: o título da linha já nomeia o recurso ("Usuários"), evita rótulo duplicado.
        usageLabel: unlimited ? `${formatInt(row.current)}` : `${formatInt(row.current)} de ${formatInt(limit)}`,
        realized: row.current,
        target: limit,
        unlimited,
        tone: at ? 'danger' : near ? 'warning' : 'success',
        statusLabel: at ? 'No limite' : near ? 'Perto do limite' : null,
        showUpgrade: at || near,
      };
    }),
  );

  /** Cartão salvo ("Visa •••• 4242") ou null — some sem permissão/sem cartão. */
  readonly cardLabel = computed<string | null>(() => {
    const pm = this.paymentMethod();
    if (!pm?.has_payment_method) return null;
    return `${pm.brand ?? 'Cartão'} •••• ${pm.last4}`;
  });

  constructor() {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    forkJoin({
      subscription: this.api.getSubscription(),
      // Uso de capacidade do plano (usuários/agentes/canais). Falha isolada → sem a seção de limites.
      capacity: this.api.getCapacity().pipe(catchError(() => of(null))),
      // Falha isolada no catálogo não derruba a tela — só fica sem preço no card (fallback null).
      plans: this.api.listPlans({ pageSize: 100 }).pipe(catchError(() => of(null))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ subscription, capacity, plans }) => {
          this.subscription.set(subscription);
          this.capacity.set(capacity?.capacity ?? []);
          if (subscription.subscription?.billing_currency) this.currency.set(subscription.subscription.billing_currency);
          const planId = subscription.plan?.id;
          const flat = plans?.data.find((p) => p.id === planId)?.prices?.find((price) => price.priceType === 'flat');
          this.planFlatPriceLabel.set(flat ? `${formatBRL(flat.amount, flat.currency)}/mês` : null);
          this.loading.set(false);
        },
        error: () => {
          this.error.set('Não foi possível carregar sua assinatura. Tente novamente.');
          this.loading.set(false);
        },
      });

    // Cartão salvo (referência ao meio de pagamento): só quem gerencia; falha isolada não derruba a página.
    if (this.auth.hasEveryPermission(['billing:payment_method:manage'])) {
      this.api
        .getPaymentMethod()
        .pipe(
          catchError(() => of(null)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((pm) => this.paymentMethod.set(pm));
    }
  }

  async cancelSubscription(): Promise<void> {
    const ok = await this.confirmDialog.confirm({
      title: 'Cancelar assinatura?',
      description:
        'Você mantém acesso ao plano atual até o fim do ciclo corrente — sem reembolso do período já pago. Pode reativar antes disso.',
      confirmLabel: 'Cancelar assinatura',
      tone: 'danger',
    });
    if (!ok) return;

    this.actionPending.set(true);
    this.api
      .cancelSubscription({ cancelAtPeriodEnd: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toast.success('Cancelamento agendado para o fim do ciclo.');
          this.actionPending.set(false);
          this.load();
        },
        error: () => {
          this.toast.error('Não foi possível cancelar a assinatura. Tente novamente.');
          this.actionPending.set(false);
        },
      });
  }

  reactivateSubscription(): void {
    this.actionPending.set(true);
    this.api
      .reactivateSubscription()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toast.success('Assinatura reativada.');
          this.actionPending.set(false);
          this.load();
        },
        error: () => {
          this.toast.error('Não foi possível reativar a assinatura. Tente novamente.');
          this.actionPending.set(false);
        },
      });
  }

  private toPlanView(): PlanView | null {
    const data = this.subscription();
    if (!data?.subscription || !data.plan) return null;
    const status = data.subscription.status;
    const renewal = formatDate(data.subscription.current_period_end);
    return {
      name: data.plan.name,
      statusLabel: STATUS_LABEL[status] ?? status,
      statusTone: STATUS_TONE[status] ?? 'neutral',
      cycleLabel: CYCLE_LABEL[data.subscription.billing_cycle] ?? data.subscription.billing_cycle,
      renewalLabel: renewal ? `Renova em ${renewal}` : null,
      periodEndLabel: renewal,
      priceLabel: this.planFlatPriceLabel(),
    };
  }
}
